import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import DashboardLayout from '../../components/DashboardLayout';
import { DashboardSkeleton } from '../../components/Skeleton';
import CountUp from '../../components/CountUp';
import PulseDot from '../../components/PulseDot';
import Icon from '../../components/Icon';
import { timeAgo, formatDateTime } from '../../lib/time';

const money = (n) => '₺' + Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const [loading, setLoading] = useState(true);
  const [revenue, setRevenue] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [silent, setSilent] = useState(null);
  const [silentDays, setSilentDays] = useState(3);
  const [q, setQ] = useState('');

  useEffect(() => {
    Promise.all([api.adminRevenue(), api.adminCustomers()])
      .then(([r, c]) => { setRevenue(r); setCustomers(c.customers); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.adminSilentAgents(silentDays).then(setSilent).catch(() => {});
  }, [silentDays]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return customers;
    return customers.filter((c) => c.email.toLowerCase().includes(s) || (c.license?.serverLabel || '').toLowerCase().includes(s));
  }, [customers, q]);

  if (loading) {
    return <DashboardLayout title={t('admin.overview_title')} crumb={t('admin.title')} heading={false}><DashboardSkeleton /></DashboardLayout>;
  }

  const subTone = (st) => (st === 'active' ? 'ok' : st === 'past_due' ? 'warn' : st === 'expired' ? 'crit' : 'idle');
  const agentTone = (st) => (st === 'active' ? 'ok' : st === 'silent' ? 'crit' : 'idle');

  return (
    <DashboardLayout title={t('admin.overview_title')} crumb={t('admin.title')} heading={false}>
      <h1 className="page-title">{t('admin.overview_title')}</h1>

      <div className="stat-row">
        <div className="card stat">
          <div className="stat-label"><Icon name="activity" size={13} /> {t('admin.revenue_total')}</div>
          <div className="stat-value">{money(revenue.totalRevenue)}</div>
          <div className="stat-meta">{revenue.transactionCount} işlem</div>
        </div>
        <div className="card stat">
          <div className="stat-label"><Icon name="trending-down" size={13} /> {t('admin.revenue_mrr')}</div>
          <div className="stat-value">{money(revenue.estimatedMrr)}</div>
        </div>
        <div className="card stat">
          <div className="stat-label"><Icon name="users" size={13} /> {t('admin.active_subs')}</div>
          <div className="stat-value"><CountUp value={revenue.activeSubscribers} /></div>
          <div className="stat-meta">{t('admin.past_due')}: {revenue.pastDue}</div>
        </div>
        <div className="card stat">
          <div className="stat-label"><Icon name="alert-triangle" size={13} /> {t('admin.churn')}</div>
          <div className="stat-value"><CountUp value={revenue.churnRate} decimals={1} suffix="%" /></div>
        </div>
      </div>

      {/* Sessiz ajanlar */}
      <div className="card chart-card">
        <div className="chart-head">
          <h3>{t('admin.silent_title')}</h3>
          <div className="lang-toggle">
            {[3, 7, 14].map((d) => (
              <button key={d} className={silentDays === d ? 'active' : ''} onClick={() => setSilentDays(d)}>{d}{t('admin.silent_days')}</button>
            ))}
          </div>
        </div>
        <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginTop: -8, marginBottom: 12 }}>{t('admin.silent_desc', { days: silentDays })}</p>
        {!silent || silent.silentAgents.length === 0 ? (
          <p style={{ color: 'var(--text-3)', fontSize: 13, margin: 0 }}>{t('admin.silent_none')}</p>
        ) : (
          <div className="list">
            {silent.silentAgents.map((s) => (
              <div className="list-row" key={s.licenseKey} style={{ padding: '10px 0' }}>
                <PulseDot tone="crit" animated={false} />
                <div className="row-main">
                  <Link to={`/admin/customers/${s.userId}`} style={{ fontWeight: 500 }}>{s.email}</Link>
                  <span style={{ color: 'var(--text-3)', fontSize: 12, marginLeft: 8 }}>{s.serverLabel || ''}</span>
                </div>
                <div className="row-time">
                  {s.neverConnected ? t('admin.never_connected') : timeAgo(s.lastHeartbeatAt, lang)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Müşteri tablosu */}
      <div className="card chart-card">
        <div className="chart-head">
          <h3>{t('admin.customers_title')} ({customers.length})</h3>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('admin.search_ph')} style={{ maxWidth: 240 }} />
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                <th style={{ padding: '8px 10px' }}>{t('admin.col_email')}</th>
                <th style={{ padding: '8px 10px' }}>{t('admin.col_plan')}</th>
                <th style={{ padding: '8px 10px' }}>{t('admin.col_status')}</th>
                <th style={{ padding: '8px 10px' }}>{t('admin.col_agent')}</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>{t('admin.col_revenue')}</th>
                <th style={{ padding: '8px 10px' }}>{t('admin.col_joined')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px' }}>
                    <Link to={`/admin/customers/${c.id}`}>{c.email}</Link>
                    {c.role === 'admin' && <span className="badge" style={{ marginLeft: 6, fontSize: 10 }}>admin</span>}
                  </td>
                  <td style={{ padding: '10px', color: 'var(--text-2)' }}>{c.subscription ? t(`checkout.plan_${c.subscription.plan}`) : <span style={{ color: 'var(--text-3)' }}>{t('admin.no_sub')}</span>}</td>
                  <td style={{ padding: '10px' }}>
                    {c.subscription ? <span className={`badge ${subTone(c.subscription.status)}`}>{t(`license.sub_${c.subscription.status}`)}</span> : '—'}
                  </td>
                  <td style={{ padding: '10px' }}>
                    {c.license ? (
                      <span className="status-line">
                        <PulseDot tone={agentTone(c.license.agentStatus)} animated={false} />
                        {t(`admin.agent_${c.license.agentStatus === 'never' ? 'never' : c.license.agentStatus}`)}
                        {c.serverCount > 1 && <span style={{ color: 'var(--text-3)', fontSize: 12, marginLeft: 6 }}>· {t('admin.slots_n', { n: c.serverCount })}</span>}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(c.totalRevenue)}</td>
                  <td style={{ padding: '10px', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{formatDateTime(c.createdAt, lang).split(' ')[0]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
